import React from 'react';
import type { Category, SidebarArticle } from '@jwp/ott-common/types/insights';

import styles from './SidebarSection.module.scss';

interface SidebarSectionProps {
  category: Category;
  title: string;
  articles: SidebarArticle[];
  isExpanded: boolean;
  onToggleExpanded: (category: Category) => void;
  onArticleClick: (article: SidebarArticle) => void;
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({ category, title, articles, isExpanded, onToggleExpanded, onArticleClick }) => {
  if (!articles.length) return null;

  const shouldShowButton = articles.length > 2;
  const articlesToShow = shouldShowButton && !isExpanded ? articles.slice(0, 2) : articles;

  const handleArticleClick = (article: SidebarArticle): void => {
    onArticleClick(article);
  };

  const handleArticleKeyDown = (e: React.KeyboardEvent, article: SidebarArticle): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleArticleClick(article);
    }
  };

  const handleToggleClick = (): void => {
    onToggleExpanded(category);
  };

  return (
    <div className={styles.sidebarSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <ul className={styles.articleList}>
        {articlesToShow.map((article) => (
          <li
            key={article.id}
            className={styles.articleItem}
            onClick={() => handleArticleClick(article)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleArticleKeyDown(e, article)}
          >
            <img width={33} height={16} src="/images/insights-list-logo.png" alt="Corrigan Sports Network" />
            <div>
              <div className={styles.articleTitle}>{article.title}</div>
              <div className={styles.articleMeta}>{article.meta}</div>
            </div>
          </li>
        ))}
      </ul>

      {shouldShowButton && (
        <button type="button" className={styles.showMore} onClick={handleToggleClick}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M11.4219 0V0.578125H15.0312L7.14062 8.45312L7.54688 8.85938L15.4219 0.96875V4.57812H16V0H11.4219ZM13.7188 15.4219H0.578125V2.28125H7.14062V1.71875H0V16H14.2812V8.85938H13.7188V15.4219Z"
              fill="white"
            />
          </svg>
          {isExpanded ? 'Show Less' : 'Show More'}
        </button>
      )}
    </div>
  );
};
